import { ApolloClient, from, InMemoryCache, split } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { type PublicClientApplication } from '@azure/msal-browser';
import { createUploadLink } from 'apollo-upload-client';
import { createClient } from 'graphql-ws';
import getToken from '@auth/msal/get-token';
import correlationIdLink from './utils/correlationId';
import errorLink from './utils/errorLink';
import backendVersionLink from './utils/backend-version-link';

const toWsUri = (httpUri: string): string => httpUri.replace(/^http/, 'ws');

export default function createApolloClient(
    msal: PublicClientApplication,
    apiUri: string,
) {
    const httpLink = createUploadLink({
        uri: apiUri,
    });

    const wsLink = new GraphQLWsLink(
        createClient({
            url: toWsUri(apiUri),
            keepAlive: 10_000,
            connectionParams: async () => {
                const token = await getToken(msal);
                return token ? { Authorization: `Bearer ${token}` } : {};
            },
        }),
    );

    const splitLink = split(
        ({ query }) => {
            const definition = getMainDefinition(query);
            return (
                definition.kind === 'OperationDefinition' &&
                definition.operation === 'subscription'
            );
        },
        wsLink,
        httpLink,
    );

    const authLink = setContext(async (_, { headers }) => {
        const token = await getToken(msal);
        return {
            headers: {
                ...headers,
                authorization: token ? `Bearer ${token}` : null,
            },
        };
    });

    return new ApolloClient({
        link: from([
            correlationIdLink,
            backendVersionLink,
            errorLink,
            authLink,
            splitLink,
        ]),
        cache: new InMemoryCache({
            typePolicies: {
                ProjectComponentNotification: {
                    keyFields: ['assetItemID', 'notificationID', 'componentID'],
                },
            },
        }),
        defaultOptions: {
            query: {
                fetchPolicy: 'cache-first',
            },
        },
    });
}
